import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
} from "@mui/material";

interface Permission {
  read: boolean;
  write: boolean;
  update: boolean;
  upload: boolean;
  delete: boolean;
}

export interface Module {
  id: string;
  slug: string;
  label: string;
  module_id?: number;
  permissions: Permission;
}

interface PermissionDialogProps {
  open: boolean;
  onClose: () => void;
  moduleData: Module[];
  togglePermission: (slug: string, action: keyof Permission) => void;
  onSubmit: () => void;
  roleName?: string;
}

const IOSSwitch = styled((props: any) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#65C466',
        opacity: 1,
        border: 0,

      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],

    },
    '&.Mui-disabled': {
      color: theme.palette.grey[400],
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),

  },
}));

export default function PermissionDialog({
  open,
  onClose,
  moduleData,
  togglePermission,
  onSubmit,
  roleName
}: PermissionDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Permissions {roleName && `- ${roleName}`}</DialogTitle>

      <DialogContent>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Read</TableCell>
                <TableCell>Write</TableCell>
                <TableCell>Update</TableCell>
                <TableCell>Upload</TableCell>
                <TableCell>Delete</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {moduleData?.map((module, index) => (
                <TableRow key={module.slug || index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{module.label}</TableCell>
                  {(["read", "write", "update", "upload", "delete"] as const).map((action) => (
                    <TableCell key={action}>
                      <IOSSwitch
                        checked={module.permissions[action]}
                        onChange={() => togglePermission(module.slug, action)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={onSubmit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
